import { createSlice } from '@reduxjs/toolkit';

import { localStorageKeys } from '@/utils/tokens';
import { saveFilterToLocalStorage } from '@/utils/localStorage';
import { LOCAL_STORAGE_FILTERS_AND_SORTING } from '@/utils/configs/healthCheckConfig';
import { migrateContractPath } from '@/utils/options/contractOptions';
import { FineStatus } from '@/apps/dispatcher/utils/reasonFine';

export interface FiltersState {
  queued: QueuedFilters;
  history: HistoryFilters;
}

export interface HistoryFilters {
  dateRange: any;
  ptaLog: string;
  keyword: string;
}

export interface QueuedFilters {
  types: number[];
  cancellationTypes: number[];
  dateRange: any;
  line: string;
  delayOverSeconds: string;
  delayUnderSeconds: string;
  block: string;
  contracts: string[][];
  flattenContracts: string[][];
  depot: string;
  id: string;
  // Done only (CBP-569). The fined / not fined sides the dispatcher picked,
  // matched against the side each work item's reason resolves to for that
  // item's own type. Empty - or both sides picked - leaves the list alone.
  fineStatuses: FineStatus[];
}

export const initialFiltersState: FiltersState = {
  queued: {
    types: [],
    cancellationTypes: [],
    dateRange: null,
    line: '',
    delayOverSeconds: '',
    delayUnderSeconds: '',
    block: '',
    contracts: [],
    flattenContracts: [],
    depot: '',
    id: '',
    fineStatuses: [],
  },
  history: {
    dateRange: null,
    ptaLog: '',
    keyword: '',
  },
};

const filtersSlice = createSlice({
  name: 'filters',
  initialState: initialFiltersState,
  reducers: {
    initializeFiltersFromStorage(state, action) {
      const userContracts = action.payload.userContracts || [];
      const storedFilters = JSON.parse(
        localStorage.getItem(localStorageKeys.workItemsFilters) || '{}'
      );

      const isNumericId = (val: string) => /^\d+$/.test(val);

      // Migrate old stored delaySeconds to delayOverSeconds if delayOverSeconds is not already set
      if (storedFilters.delaySeconds && !storedFilters.delayOverSeconds) {
        storedFilters.delayOverSeconds = storedFilters.delaySeconds;
        delete storedFilters.delaySeconds;
      }

      // Migrate legacy/typo region path segments to current canonical IDs
      if (storedFilters.contracts) {
        storedFilters.contracts =
          storedFilters.contracts.map(migrateContractPath);
      }
      if (storedFilters.flattenContracts) {
        storedFilters.flattenContracts =
          storedFilters.flattenContracts.map(migrateContractPath);
      }

      const validContracts = (storedFilters.contracts || []).filter(
        (contractPath: string[]) => {
          if (!contractPath || contractPath.length === 0) {
            return false;
          }
          const leaf = contractPath[contractPath.length - 1];
          if (leaf && isNumericId(leaf)) {
            return userContracts.includes(Number(leaf));
          }
          return (storedFilters.flattenContracts || []).some(
            (flatPath: string[]) => {
              if (
                flatPath.length >= contractPath.length &&
                flatPath
                  .slice(0, contractPath.length)
                  .every((v, i) => v === contractPath[i])
              ) {
                const flatLeaf = flatPath[flatPath.length - 1];
                if (!flatLeaf) {
                  return false;
                }
                return (
                  isNumericId(flatLeaf) &&
                  userContracts.includes(Number(flatLeaf))
                );
              }
              return false;
            }
          );
        }
      );

      const validFlattenContracts = (
        storedFilters.flattenContracts || []
      ).filter((contractPath: string[]) => {
        if (!contractPath || contractPath.length === 0) {
          return false;
        }
        const leaf = contractPath[contractPath.length - 1];
        if (!leaf) {
          return;
        }
        return isNumericId(leaf) && userContracts.includes(Number(leaf));
      });

      const validatedFilters = {
        ...storedFilters,
        contracts: validContracts,
        flattenContracts: validFlattenContracts,
      };

      if (
        validContracts.length !== (storedFilters.contracts?.length || 0) ||
        validFlattenContracts.length !==
          (storedFilters.flattenContracts?.length || 0)
      ) {
        saveFilterToLocalStorage(
          localStorageKeys.workItemsFilters,
          validatedFilters,
          LOCAL_STORAGE_FILTERS_AND_SORTING
        );
      }

      state.queued = { ...state.queued, ...validatedFilters };
    },
    setQueuedFilters(state, action) {
      state.queued = { ...state.queued, ...action.payload };
      saveFilterToLocalStorage(
        localStorageKeys.workItemsFilters,
        state.queued,
        LOCAL_STORAGE_FILTERS_AND_SORTING
      );
    },
    resetQueuedFilters(state, action) {
      if (action.payload.resetAll) {
        saveFilterToLocalStorage(
          localStorageKeys.workItemsFilters,
          {
            contracts: [],
            flattenContracts: [],
          },
          LOCAL_STORAGE_FILTERS_AND_SORTING
        );
        state.queued = initialFiltersState.queued;
      } else {
        const currentContracts = state.queued.contracts;
        const currentFlattenContracts = state.queued.flattenContracts;
        const updatedState = {
          ...initialFiltersState.queued,
          contracts: currentContracts,
          flattenContracts: currentFlattenContracts,
        };
        saveFilterToLocalStorage(
          localStorageKeys.workItemsFilters,
          updatedState,
          LOCAL_STORAGE_FILTERS_AND_SORTING
        );
        state.queued = updatedState;
      }
    },
    resetContract(state) {
      const updatedState = {
        ...state.queued,
        contracts: [],
        flattenContracts: [],
      };
      saveFilterToLocalStorage(
        localStorageKeys.workItemsFilters,
        updatedState,
        LOCAL_STORAGE_FILTERS_AND_SORTING
      );
      state.queued = updatedState;
    },
    setHistoryFilters(state, action) {
      state.history = { ...state.history, ...action.payload };
    },
    resetHistoryFilters(state) {
      state.history = initialFiltersState.history;
    },
  },
});

export const {
  initializeFiltersFromStorage,
  setQueuedFilters,
  resetQueuedFilters,
  resetContract,
  setHistoryFilters,
  resetHistoryFilters,
} = filtersSlice.actions;

export default filtersSlice.reducer;
